import React from "react";
import {
    Box,
    IconButton,
    LinearProgress,
    linearProgressClasses,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import styled from "styled-components";
import { differenceInDays, differenceInHours, format } from "date-fns";

const H = styled.h3`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 0;
`;

const P = styled.p`
    font-size: 12px;
    font-weight: 300;
    max-width: 95%;
    height: 46px;
    color: ${({ theme }) => theme.palette.secondary.main};
    padding-bottom: 16px;
    margin: 0;
`;

const P2 = styled.p`
    font-size: 14px;
    font-weight: 500;
    max-width: 95%;
    color: ${({ theme, progress, target, targetComparison, isAverage }) => {
        if ((targetComparison && targetComparison === "gte") || !targetComparison) {
            if (!isAverage) return theme.palette.secondary.main;
            if (progress === null) return theme.palette.secondary.main;
            if (progress === 0) return theme.palette.secondary.main;
            if (progress > 0 && progress < 1) return theme.palette.secondary.main;
            if (progress >= target) return theme.palette.primary.main;
            if (progress < target) return theme.palette.background.wellbeing;
        }
        if (targetComparison && targetComparison === "lte") {
            if (!isAverage) return theme.palette.secondary.main;
            if (progress === null) return theme.palette.secondary.main;
            if (progress === 0) return theme.palette.secondary.main;
            if (progress > 0 && progress < 1) return theme.palette.secondary.main;
            if (progress < target) return theme.palette.primary.main;
            if (progress >= target) return theme.palette.background.wellbeing;
        }
        return null;
    }};
    margin: 0;
`;

const P3 = styled.p`
    font-size: 10px;
    opacity: 0.5;
    font-weight: 300;
    max-width: 95%;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 0;
`;

const P4 = styled.p`
    font-size: 12px;
    font-weight: 300;
    max-width: 95%;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 0;
`;
const MissionPaper = styled(Paper)`
    width: 288px;
    min-height: 139px;
    border-radius: 14px;
`;

const HeaderStack = styled(Stack)`
    width: 100%;
    background-color: #5b5b5b;
    padding: 4px 8px 2px 16px;
    border-radius: 14px 14px 0 0;
`;

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === "dark" ? "#59C36A" : "#308fe8",
    },
}));

const TooltipTitle = (startDate, endDate) => {
    return (
        <Stack direction="column" style={{ width: 130 }}>
            <Stack direction="column" style={{ width: "100%" }}>
                <P3>Started on</P3>
                {startDate && <P4>{format(new Date(startDate * 1000), "M/d/yyyy HH:mmaaa")}</P4>}
            </Stack>
            <Stack direction="column" style={{ width: "100%" }}>
                <P3>Running till</P3>
                {endDate && <P4>{format(new Date(endDate * 1000), "M/d/yyyy HH:mmaaa")}</P4>}
            </Stack>
        </Stack>
    );
};

const calculateAvgLinearProgressValue = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const missionDuration = differenceInDays(endDate * 1000, startDate * 1000);
    const daysLeft = Math.abs(differenceInDays(startDate * 1000, Date.now()));
    const completionPercentage = (daysLeft / missionDuration) * 100;
    return completionPercentage;
};

const calculateTimeLeft = (startDate, endDate) => {
    const hoursLeft = Math.abs(differenceInHours(endDate * 1000, Date.now()));
    if (hoursLeft >= 24) {
        return (
            <>
                <Typography
                    style={{ fontSize: "16px", fontWeight: "600" }}
                    variant="body2"
                    color="text.secondary"
                >
                    {differenceInDays(endDate * 1000, Date.now())}
                </Typography>
                <Stack direction="column">
                    <Typography style={{ fontSize: "10px" }} variant="body2" color="text.secondary">
                        days
                    </Typography>
                    <Typography style={{ fontSize: "10px" }} variant="body2" color="text.secondary">
                        left
                    </Typography>
                </Stack>
            </>
        );
    }
    return (
        <>
            <Typography
                style={{ fontSize: "16px", fontWeight: "600" }}
                variant="body2"
                color="text.secondary"
            >
                {hoursLeft < 1 ? ">1" : hoursLeft}
            </Typography>
            <Stack direction="column">
                <Typography style={{ fontSize: "10px" }} variant="body2" color="text.secondary">
                    {hoursLeft <= 1 ? "hour" : "hours"}
                </Typography>
                <Typography style={{ fontSize: "10px" }} variant="body2" color="text.secondary">
                    left
                </Typography>
            </Stack>
        </>
    );
};

const MissionCard = ({
    title,
    completion,
    progress,
    goal,
    handleDeleteModal,
    startDate,
    endDate,
    target,
    targetComparison,
    missionId,
}) => {
    const [isAverage, setIsAverage] = React.useState(undefined);

    React.useEffect(() => {
        if (missionId && missionId.substring(0, 3) === "avg") {
            setIsAverage(true);
        }
    }, [missionId]);

    return (
        <MissionPaper>
            <HeaderStack direction="row" justifyContent="space-between" alignItems="center" pb={1}>
                <H>{title}</H>
                <IconButton onClick={handleDeleteModal} aria-label="delete">
                    <DeleteForeverIcon fontSize="small" />
                </IconButton>
            </HeaderStack>
            <Box style={{ padding: "12px" }}>
                <P>
                    {goal.split(/(__target__|__target_comparison__)/).map((part) => {
                        if (part === "__target__") {
                            return <strong key={part}>{target}</strong>;
                        }
                        if (part === "__target_comparison__") {
                            if (!targetComparison || targetComparison === "gte") {
                                return <span key={part}>greater than</span>;
                            }
                            return <span key={part}>less than</span>;
                        }
                        return <span key={part}>{part}</span>;
                    })}
                </P>
                <Stack direction="row" alignItems="center" style={{ width: "100%" }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        style={{ width: "33%" }}
                        spacing={0.5}
                    >
                        <P3>Current</P3>
                        <P2
                            progress={progress}
                            target={target}
                            targetComparison={targetComparison}
                            isAverage={isAverage}
                        >
                            {progress === null ? (
                                <Tooltip style={{ zIndex: 200 }} title="No data collected yet">
                                    <P2>-</P2>
                                </Tooltip>
                            ) : (
                                progress && progress.toFixed(2)
                            )}
                        </P2>
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        style={{ width: "33%" }}
                        spacing={0.5}
                    >
                        <P3>Target</P3>
                        <P2 style={{ fontSize: "14px", fontWeight: "400" }}>{target}</P2>
                    </Stack>
                </Stack>
                {isAverage ? (
                    <Box sx={{ display: "flex", alignItems: "center", paddingTop: "8px" }}>
                        <Box sx={{ width: "100%", mr: 1 }}>
                            <BorderLinearProgress
                                aria-label="mission progress"
                                variant="determinate"
                                value={calculateAvgLinearProgressValue(startDate, endDate)}
                            />
                        </Box>
                        <Box sx={{ minWidth: 41 }}>
                            <Tooltip
                                style={{ zIndex: 200 }}
                                title={TooltipTitle(startDate, endDate)}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                >
                                    {calculateTimeLeft(startDate, endDate)}
                                </Stack>
                            </Tooltip>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", paddingTop: "16px" }}>
                        <Box sx={{ width: "100%", mr: 1 }}>
                            <BorderLinearProgress variant="determinate" value={completion} />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                            <Tooltip
                                style={{ zIndex: 200 }}
                                title={TooltipTitle(startDate, endDate)}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {Math.round(completion)}%
                                </Typography>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Box>
        </MissionPaper>
    );
};

export default MissionCard;
